import { createElement as $$createElement, setStyle as $$setStyle, setDataset as $$setDataset, setEvent as $$setEvent, delegateEvent as $$delegateEvent, setHTMLProp as $$setHTMLProp, setHTMLAttr as $$setHTMLAttr, setHTMLProps as $$setHTMLProps, setHTMLAttrs as $$setHTMLAttrs, createTextNode as $$createTextNode, updateText as $$updateText, insertNode as $$insertNode, ForNode as $$ForNode, CondNode as $$CondNode, ExpNode as $$ExpNode, EnvNode as $$EnvNode, TryNode as $$TryNode, SnippetNode as $$SnippetNode, PropView as $$PropView, render as $$render } from "@dlightjs/dlight";
import { DLightViewProp, Pretty, Typed, View, required } from "@dlightjs/dlight";
import { Navigator } from "./Navigator";
import { getHashLocation, getHistoryLocation, isDLightClass, paramObjCompare, trimPath } from "./utils";
import { RouteGroupEnv, RouteGroupProps, RouteInfo, RouteOption } from "./types";
const rawHistoryPushState = history.pushState;
let historyPushStateFuncs: Array<() => any> = [];
class RouteGroup extends View implements RouteGroupProps {
  constructor() {
    super();
    this.homeUrl = this.homeUrl.bind(this);
    this.collectRoutes = this.collectRoutes.bind(this);
    this.updateRoute = this.updateRoute.bind(this);
    this.changePath = this.changePath.bind(this);
    this.historyChangeListen = this.historyChangeListen.bind(this);
    this.hashChangeListen = this.hashChangeListen.bind(this);
    this.willMount = this.willMount.bind(this);
    this.didMount = this.didMount.bind(this);
    this.willUnmount = this.willUnmount.bind(this);
  }
  $e$_dl_router_baseUrl;
  _dl_router_baseUrl = "/";
  $$_dl_router_baseUrl = 1;
  $p$mode;
  mode: RouteGroupProps["mode"] = "history";
  $$mode = 2;
  $s$mode = ["currentPath"];
  $p$loading;
  loading?: RouteGroupProps["loading"];
  $p$guard;
  guard?: RouteGroupProps["guard"];
  $p$onPathUpdate;
  onPathUpdate?: RouteGroupProps["onPathUpdate"];
  $p$afterEnter;
  afterEnter?: RouteGroupProps["afterEnter"];
  $p$beforeLeave;
  beforeLeave?: RouteGroupProps["beforeLeave"];
  get routes() {
    return this._$children;
  }
  routeMap: Record<string, RouteOption> = {};
  hitPath: string = " NONE ";
  $$hitPath = 256;
  prevInfo: RouteInfo = {
    path: ""
  };
  currentPath: string;
  $$currentPath = 1024;
  get $f$currentPath() {
    if (this._$cache("currentPath", [this.mode])) {
      return this.currentPath;
    }
    return this.mode === "hash" ? getHashLocation() : getHistoryLocation();
  }
  viewHolder: {
    view?: DLightViewProp | DLightViewProp | DLightViewProp;
    propView: boolean;
    params?: Record<string, string>;
  } = {
    propView: true
  };
  $$viewHolder = 2048;
  homeUrl(path: string) {
    // ---- Replace the leading "~/" with the base url
    return path.replace(/^~/, this._dl_router_baseUrl);
  }
  loaded = false;
  collectRoutes(key: string, value: RouteOption) {
    this.routeMap[key] = value;
    // ---- After the first render, setting the routeMap means change lazy or comp
    //      So we need to update the route and get the new view
    if (this.loaded) this.updateRoute(this.prevInfo.path);
  }
  async updateRoute(currUrl: string) {
    if (!this.loaded) this.loaded = true;
    const fullCurrUrl = "/" + currUrl;
    currUrl = trimPath(fullCurrUrl.replace(new RegExp(`^${this._dl_router_baseUrl}`), ""));
    for (const [targetUrl, {
      comp,
      children,
      loading,
      redirect,
      info
    }] of Object.entries(this.routeMap)) {
      const newTargetUrl = this.homeUrl(targetUrl);
      // ---- Check if the route is a root route
      const isRootRoute = newTargetUrl === "" && currUrl === "";

      // ---- Check if the current path is a parent path of the target path
      const isParentPath = currUrl.startsWith(newTargetUrl + "/");
      let pathMatch: RegExpMatchArray | null = null;
      let params: Record<string, string> | undefined;
      if (!isParentPath) {
        // ---- Using regex to check if the current path matches the target path
        pathMatch = currUrl.match(new RegExp(`^${newTargetUrl}$`));
        if (pathMatch) params = pathMatch.groups;
      }
      const isMatch = isRootRoute || isParentPath || !!pathMatch;
      if (!isMatch) continue;

      // ---- Same order below, children > comp
      if (targetUrl === this.hitPath && paramObjCompare(params, this.viewHolder.params)) {
        if (children || !isDLightClass(comp) || this.viewHolder.view === comp) {
          // ---- When condition is the same, ignore
          return;
        }
      }

      // ---- Check guard
      const currInfo = {
        ...info,
        path: fullCurrUrl
      };
      if (this.guard) {
        const canGo = await this.guard(currInfo, this.prevInfo, this._dl_router_baseUrl);
        if (canGo === false) {
          this.navigator.to(this.prevInfo.path);
          return;
        }
        if (typeof canGo === "string") {
          this.navigator.to(this.homeUrl(canGo));
          return;
        }
      }
      // ---- Check redirect
      if (redirect) {
        this.navigator.to(this.homeUrl(redirect));
        return;
      }

      // ---- Life cycle
      this.beforeLeave && (await this.beforeLeave(currInfo, this.prevInfo, this._dl_router_baseUrl));
      const afterEnter = async () => {
        this.afterEnter && (await this.afterEnter(currInfo, this.prevInfo, this._dl_router_baseUrl));
      };
      if (children) {
        this._$ud(this.viewHolder = {
          view: children,
          propView: true
        }, "viewHolder");
        afterEnter();
      } else if (comp) {
        if (isDLightClass(comp)) {
          // ---- It's a component class
          this._$ud(this.viewHolder = {
            view: comp,
            propView: false,
            params
          }, "viewHolder");
          afterEnter();
        } else {
          // ---- It's a lazy loaded component
          const newLoading = loading ?? this.loading;
          if (newLoading) {
            this._$ud(this.viewHolder = {
              view: newLoading,
              propView: !!(newLoading as any).propViewFunc
            }, "viewHolder");
          }
          comp().then((module: any) => {
            this._$ud(this.viewHolder = {
              view: module.default,
              propView: false,
              params
            }, "viewHolder");
            afterEnter();
          });
        }
      }
      this._$ud(this.hitPath = targetUrl, "hitPath");
      this.prevInfo = {
        path: fullCurrUrl,
        ...params
      };
      return;
    }
  }
  navigator = new Navigator();
  $$navigator = 8192;
  changePath(path: string) {
    if (path === this.currentPath) return;
    this._$ud(this.currentPath = path, "currentPath");
    this.onPathUpdate && this.onPathUpdate(this.currentPath);
  }
  firstIn = false;
  historyChangeListen() {
    const newUrl = getHistoryLocation();
    this.changePath(newUrl);
    if ("/" + newUrl === this.prevInfo.path) return;
    this.updateRoute(newUrl);
  }
  hashChangeListen() {
    const newUrl = getHashLocation();
    this.changePath(newUrl);
    if ("/" + newUrl === this.prevInfo.path) return;
    this.updateRoute(newUrl);
  }
  willMount() {
    this._$ud(this.navigator.mode = this.mode!, "navigator");
    Promise.resolve().then(() => {
      if (this.mode === "hash") {
        this.hashChangeListen();
      } else {
        this.historyChangeListen();
      }
    });
  }
  didMount() {
    if (this.mode === "hash") {
      addEventListener("load", this.hashChangeListen);
      addEventListener("hashchange", this.hashChangeListen);
      return;
    }
    addEventListener("load", this.historyChangeListen);
    addEventListener("popstate", this.historyChangeListen);

    // ---- Listen to pushState
    historyPushStateFuncs.push(this.historyChangeListen);
    history.pushState = new Proxy(rawHistoryPushState, {
      apply(target, thisArg, argArray) {
        const res = target.apply(thisArg, (argArray as any));
        historyPushStateFuncs.forEach(func => func());
        return res;
      }
    });
  }
  willUnmount() {
    if (this.mode === "hash") {
      removeEventListener("load", this.hashChangeListen);
      removeEventListener("hashchange", this.hashChangeListen);
      return;
    }
    removeEventListener("load", this.historyChangeListen);
    removeEventListener("popstate", this.historyChangeListen);
    // ---- Delete current historyChangeListen
    historyPushStateFuncs = historyPushStateFuncs.filter(func => func !== this.historyChangeListen);
    if (historyPushStateFuncs.length > 0) {
      history.pushState = new Proxy(rawHistoryPushState, {
        apply(target, thisArg, argArray) {
          const res = target.apply(thisArg, (argArray as any));
          historyPushStateFuncs.forEach(func => func());
          return res;
        }
      });
    } else {
      history.pushState = rawHistoryPushState;
    }
  }
  Body() {
    let $node0, $node1, $node2;
    this._$update = $changed => {
      if ($changed & 257) {
        $node0 && $node0.updateEnv("_dl_router_baseUrl", () => "/" + trimPath(`${this._dl_router_baseUrl}/${this.hitPath}`), [this._dl_router_baseUrl, this.hitPath]);
      }
      if ($changed & 1026) {
        $node0 && $node0.updateEnv("path", () => this.currentPath, [this.currentPath]);
      }
      if ($changed & 2048) {
        $node2 && $node2.updateCond();
      }
      if ($changed & 8192) {
        $node0 && $node0.updateEnv("navigator", () => this.navigator, [this.navigator]);
      }
      $node2 && $node2.update($changed);
    };
    $node0 = new $$EnvNode({
      _dl_router_routesCollect: this.collectRoutes,
      _dl_router_baseUrl: "/" + trimPath(`${this._dl_router_baseUrl}/${this.hitPath}`),
      path: this.currentPath,
      navigator: this.navigator
    }, {
      _dl_router_routesCollect: [],
      _dl_router_baseUrl: [this._dl_router_baseUrl, this.hitPath],
      path: [this.currentPath],
      navigator: [this.navigator]
    });
    $node1 = new $$ExpNode(this.routes, []);
    $node2 = new $$CondNode(2048, $thisCond => {
      if (this.viewHolder.propView) {
        if ($thisCond.cond === 0) {
          $thisCond.didntChange = true;
          return [];
        }
        $thisCond.cond = 0;
        let $node0;
        $thisCond.updateFunc = $changed => {
          if ($changed & 2048) {
            $node0 && $node0.update(() => this.viewHolder.view, [this.viewHolder?.view]);
          }
        };
        $node0 = new $$ExpNode(this.viewHolder.view, [this.viewHolder?.view]);
        return $thisCond.cond === 0 ? [$node0] : $thisCond.updateCond();
      } else {
        if ($thisCond.cond === 1) {
          $thisCond.didntChange = true;
          return [];
        }
        $thisCond.cond = 1;
        let $node0, $node1;
        $thisCond.updateFunc = $changed => {
          if ($changed & 2048) {
            $node1 && $node1.update(() => $node0, [this.viewHolder?.view]);
          }
          $node0 && $node0.update($changed);
        };
        $node0 = new $$PropView($addUpdate => {
          let $node0;
          $addUpdate($changed => {
            if ($changed & 2048) {
              $node0 && $node0._$setProps(() => this.viewHolder.params, [this.viewHolder?.params]);
            }
          });
          $node0 = new (this.viewHolder.view as any)();
          $node0._$init(null, null, null, null);
          return [$node0];
        });
        $node1 = new $$ExpNode($node0, [this.viewHolder?.view]);
        return $thisCond.cond === 1 ? [$node1] : $thisCond.updateCond();
      }
    });
    $node0.initNodes([$node1, $node2]);
    return [$node0];
  }
}
export default ((RouteGroup as Pretty) as Typed<RouteGroupProps>);